import React, { useEffect, useRef } from "react";
import Navbar from '../Components/Navbar';
import { isMobile } from "react-device-detect";
import GLightbox from 'glightbox';
import 'glightbox/dist/css/glightbox.css';
import Footer from "../Components/Footer";
import Typed from "typed.js";
import { motion } from "motion/react";
import Core from "../Components/Core";

const Careers = () => {
  const typedEl = useRef(null);

  useEffect(() => {
    const typed = new Typed(typedEl.current, {
      strings: [
        "Join the Ebun Heritage Team",
        "Inspiring Minds. Building Futures.",
        "Be Part of a Legacy of Excellence",
      ],
      typeSpeed: 50,
      backSpeed: 30,
      backDelay: 2000,
      loop: true,
      showCursor: true,
      cursorChar: "<span class='text-warning'>|</span>",
    });

    return () => {
      typed.destroy();
    };
  }, []);

  return (
    <div>
      <Navbar />

      {/* HERO SECTION */}
      <motion.section
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        viewport={{ once: true }}
        className="bg-white pt-lg-6 pt-4 pb-lg-6 pb-4 px-lg-6 px-4"
        style={{
          backgroundImage: "url('../../assets/images/background/slide/head1.jpg')",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundPosition: "80% 60%",
          opacity: 0.95,
        }}
      >
        <div className="container">
          <div className="row align-items-center py-lg-12 py-8 px-lg-0">
            <div className="col-lg-12 text-center">
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="fs-1 fw-semibold text-dark mb-3"
              >
                <span ref={typedEl}></span>
              </motion.div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* CAREERS OVERVIEW SECTION */}
      <section className="bg-white pt-lg-6 pt-4 pb-lg-8 pb-8 px-lg-6 px-4">
        <div className="container">
          <div className="row gx-8 py-lg-6 px-lg-0">

            {/* Why Work With Us */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="col-lg-6"
            >
              <div className="fs-t2 text-gray-800 fw-semibold mb-lg-4 mb-4 text-lg-start text-center">
                Why Work With Us?
              </div>

              <div className={`fs-4 text-dark ${isMobile ? '' : 'text-justify'}`}>
                At Ebun Heritage Schools, we believe that great educators and staff 
                are the foundation of academic excellence and character development.
                <br /><br />
                We provide a supportive, faith-driven, and growth-oriented environment 
                where teachers and staff are empowered to thrive professionally and personally.
                <br /><br />
                Our commitment to innovation, discipline, and integrity makes 
                Ebun Heritage Schools a rewarding place to build your career.
              </div>
            </motion.div>

            {/* Our Culture */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="col-lg-6 mt-lg-0 mt-6"
            >
              <div className="fs-t2 text-gray-800 fw-semibold mb-lg-4 mb-4 text-lg-start text-center">
                Our Work Culture
              </div>

              <div className={`fs-4 text-dark ${isMobile ? '' : 'text-justify'}`}>
                ✔ Christ-centered values <br />
                ✔ Commitment to excellence <br />
                ✔ Continuous professional development <br />
                ✔ Team collaboration and mentorship <br />
                ✔ Respect, discipline, and accountability
                <br /><br />
                We foster a dynamic learning environment where innovation 
                and creativity are encouraged.
              </div>
            </motion.div>

            {/* Career Opportunities */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="col-lg-12 mt-lg-8 mt-6"
            >
              <div className="fs-t2 text-gray-700 fw-semibold mb-lg-4 mb-4 text-lg-start text-center">
                Career Opportunities
              </div>

              <div className={`fs-4 text-dark ${isMobile ? 'px-4' : 'text-justify'}`}>
                We regularly seek qualified and passionate professionals for:
                <br /><br />
                ✔ Subject Teachers (Primary & Secondary) <br />
                ✔ Early Years Educators <br />
                ✔ Administrative Staff <br />
                ✔ ICT Personnel <br />
                ✔ Guidance & Counseling Officers <br />
                ✔ Support Staff
                <br /><br />
                If you are passionate about shaping young minds and 
                nurturing God-given potential, we would love to hear from you.
                <br /><br />
                To apply, please send your application and updated CV to our official 
                school email address; <span className="fw-semibold">admin@ebunheritageschool.com</span> <br/>or visit any of our school campuses for submission.
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* APPLICATION SECTION */}
      <section className="bg-back pt-lg-6 pt-8 pb-lg-6 pb-4 px-lg-6 px-0">
        <div className="container">

          {/* PROFESSIONAL GROWTH SECTION */}
          <div className="row align-items-center pt-lg-0 pt-6 pb-lg-0 px-lg-0">

            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="col-lg-6 mb-6 mb-lg-0"
            >
              <div className="fs-t2 text-gray-800 fw-semibold mb-lg-4 mb-4 text-lg-start text-center">
                Professional Growth & Development
              </div>

              <div className={`fs-4 text-dark ${isMobile ? 'px-4' : 'text-justify'}`}>
                At Ebun Heritage Schools, we invest in our people.
                <br /><br />
                Our staff benefit from ongoing training, workshops,
                curriculum development programs, and leadership mentoring.
                <br /><br />
                We believe that when educators grow, students flourish.
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="col-lg-6 text-center"
            >
              <img
                src="../../assets/images/background/his.svg"
                alt="Careers at Ebun Heritage"
                width={500}
                className="img-fluid mb-lg-4"
              />
            </motion.div>

          </div>
        </div>
      </section>

      <Core />
      <Footer />
    </div>
  );
};

export default Careers;
